/********************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2024
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} **/

import jsonPointer from 'jsonpointer';
import { isEmpty, get, uniq } from 'lodash';
import _has from 'lodash/has';
import APIFactory from './api-factory';
import { getAPIType, APITypes } from './APITypes';
import { MarkupTypes } from './MarkupTypes';

const CATALOG_URL = '$(catalog.url)';

export default class ApiCommon {
  constructor(api) {
    this.api = api;
    if (this.isSupported(api)) {
      this.version = this.getVersion(api);
    } else {
      this.version = null;
    }
  }

  getSecurity(operation = {}) {
    // if an operation is provided, use that security instead of the api security
    let security = operation.security ?? this.api.security ?? [];
    if (Array.isArray(security)) {
      security = security.filter((value) => Object.keys(value).length !== 0);
    }
    return security;
  }

  /**
   * Asserts that the correct handler object based on the current handler and the version of
   * the provided open api.  If the versions don't match then a new handler object is returned
   * which matches the version of the new openApi
   */
  assertHandler(openApi) {
    // get a new handler if the version of the selected api is different from the previous one
    if (this.version !== this.getVersion(openApi)) {
      return new APIFactory(openApi);
    } else {
      return this;
    }
  }

  /**
   * Determines the version of the openApi document based on either the 'swagger'
   * 'openapi', or 'asyncapi' property. If none of these are present, version 2.0 is used
   * as the default.
   */
  getVersion(openApi) {
    if (openApi.swagger) {
      return openApi.swagger;
    } else if (openApi.openapi) {
      return openApi.openapi;
    } else if (openApi.asyncapi) {
      return openApi.asyncapi;
    } else {
      return '2.0';
    }
  }

  /**
   * Returns the label to use for the spec
   *
   * @param api
   * @returns {string|null}
   */
  getSpecLabel(api) {
    const type = this.getType(api);
    if (type === 'REST') {
      if (api.openapi) {
        return 'OAS3';
      } else {
        return 'OAS2';
      }
    } else {
      return null;
    }
  }

  /**
   * Determines the type of the openApi document based on the 'x-ibm-configuration'
   * 'type' property. If this is not present, rest is returned as the default.
   */
  getType(api) {
    return getAPIType(api);
  }

  getMarkupType() {
    const type = this.getType(this.api);
    const version = this.getVersion(this.api);

    switch (type) {
      case APITypes.rest:
      case APITypes.wsdl: {
        return version.startsWith('1.') || version.startsWith('2.') ? MarkupTypes.markdown : MarkupTypes.commonmark;
      }
      default:
        return MarkupTypes.commonmark;
    }
  }

  /**
   * Tests to see if API is SOAP
   */
  isSOAP(openApi) {
    if (openApi['x-ibm-configuration'] && openApi['x-ibm-configuration'].type === 'wsdl') {
      return true;
    }
    return false;
  }

  /**
   * Tests to see if API is GraphQL
   */
  isGraphQL(openApi) {
    if (openApi['x-ibm-configuration'] && openApi['x-ibm-configuration'].type === 'graphql') {
      return true;
    }
    return false;
  }

  /**
   * Tests to see if API is AsyncAPI
   */
  isAsync(openApi) {
    const type = this.getType(openApi);
    if (type === APITypes.asyncapi) {
      return true;
    }
    return false;
  }

  /**
   * test to see if API is testable
   */
  isTestable(openApi) {
    if (
      (openApi['x-ibm-configuration'] && _has(openApi['x-ibm-configuration'], 'testable') && !openApi['x-ibm-configuration'].testable) ||
      (openApi['info'] && _has(openApi['info'], 'x-try-it-out-enabled') && !openApi['info']['x-try-it-out-enabled'])
    ) {
      return false;
    }
    return true;
  }

  /**
   * get endpoint types
   */
  getEndpointTypes(types) {
    if (!types || types.length < 1) {
      return;
    }

    // capitalise each type
    const capitalisedTypes = types.map((type) => {
      type = type.split('');
      type[0] = type[0].toUpperCase();
      type = type.join('');
      return type;
    });

    // create comma separated list
    let typeString = capitalisedTypes.join(', ');

    return typeString;
  }

  /**
   * get the path for testing
   * if SOAP it is empty string. basePath should already be in the endpoint
   */
  getOperationPath(openApi, operationPath) {
    if (!operationPath || !openApi) {
      return;
    }

    let result = '';

    if (this.isSOAP(openApi)) {
      result = '';
    } else {
      result = operationPath;
    }

    return result;
  }

  /**
   * returns all parameters
   */
  getParameters(operation, path, contentType, openApi) {
    if (!operation || !openApi || !this.isSupported(openApi)) {
      return;
    }

    let parameters = [];
    let dereferencedParameters = {};
    if (operation.parameters) {
      parameters = parameters.concat(operation.parameters);
    }

    if (openApi.paths && openApi.paths[path] && openApi.paths[path].parameters) {
      parameters = parameters.concat(openApi.paths[path].parameters);
    }

    let self = this;
    if (operation.requestBody) {
      // we have an OpenAPI 3.0 requestBody
      if (contentType.startsWith('multipart/') || contentType === 'application/x-www-form-urlencoded') {
        parameters = parameters.concat(this.getFormDataParameters(operation, path, contentType, openApi));
      } else {
        dereferencedParameters['body'] = this.getBodyParameter(operation, path, contentType, openApi);
      }
    }

    for (let i = 0; i < parameters.length; i++) {
      parameters[i] = JSON.parse(JSON.stringify(parameters[i]));
    }

    parameters.forEach(function (parameter) {
      while (parameter.$ref || (parameter.schema && parameter.schema.$ref)) {
        let missing = false;
        if (parameter.$ref) {
          let parameterName = parameter.$ref.split('/').pop();
          let dereffed = self.getLocalReference(parameter.$ref, openApi);
          if (dereffed && !dereffed.error) {
            delete parameter.$ref;
            parameter = Object.assign(parameter, dereffed);
          } else {
            console.warn('Missing reference: ' + parameter.$ref);
            missing = true;
            parameter = {
              name: parameterName,
              description: 'missing',
              schema: {
                type: 'unknown'
              }
            };
          }
        } else if (parameter.schema && parameter.schema.$ref) {
          let parameterName = parameter.schema.$ref.split('/').pop();
          let dereffed = self.getLocalReference(parameter.schema.$ref, openApi);
          if (dereffed && !dereffed.error) {
            let param = JSON.parse(JSON.stringify(parameter));
            param.schema = dereffed;
            parameter = param;
          } else {
            missing = true;
            console.warn('Missing reference: ' + parameter.schema.$ref);
            parameter = {
              name: parameterName,
              description: 'missing',
              schema: {
                type: 'unknown'
              }
            };
          }
        }
        if (missing) {
          break;
        }
      }
      dereferencedParameters[parameter.name] = parameter;
    });
    return dereferencedParameters;
  }

  /**
   * Return true if reserved headers in this operation
   *
   * @param parameterObject
   * @returns {boolean}
   */
  containsReservedParameters(parameterObject) {
    let removedHeaders = false;
    if (parameterObject && Object.keys(parameterObject) !== undefined && Object.keys(parameterObject).length > 0) {
      Object.keys(parameterObject).forEach((key) => {
        if (parameterObject[key].in && parameterObject[key].in === 'header' && this.reservedHeaders().includes(parameterObject[key].name.toLowerCase())) {
          removedHeaders = true;
        }
      });
    }
    return removedHeaders;
  }

  /**
   * Remove the reserved headers from the operation
   *
   * @param parameterObject
   * @returns {*}
   */
  removeReservedParameters(parameterObject) {
    if (parameterObject && Object.keys(parameterObject) !== undefined && Object.keys(parameterObject).length > 0) {
      Object.keys(parameterObject).forEach((key) => {
        if (parameterObject[key].in && parameterObject[key].in === 'header' && this.reservedHeaders().includes(parameterObject[key].name.toLowerCase())) {
          delete parameterObject[key];
        }
      });
    }
    return parameterObject;
  }

  /**
   * Remove the reserved headers from the list
   *
   * @param parameterNameList
   * @returns {*}
   */
  removeReservedParameterNames(parameterNameList) {
    if (parameterNameList !== undefined && parameterNameList.length > 0) {
      parameterNameList = parameterNameList.filter(function (element) {
        let remove = this.reservedHeaders().includes(element.toLowerCase());
        return !remove;
      }, this);
    }
    return parameterNameList;
  }

  /**
   * Headers not allowed to send with REST Calls as per the spec
   *
   * @returns {string[]}
   */
  reservedHeaders() {
    return [
      'accept-charset',
      'accept-encoding',
      'access-control-request-headers',
      'access-control-request-method',
      'connection',
      'content-length',
      'cookie',
      'cookie2',
      'date',
      'dnt',
      'expect',
      'host',
      'keep-alive',
      'origin',
      'referer',
      'te',
      'trailer',
      'transfer-encoding',
      'upgrade',
      'via'
    ];
  }

  /**
   * returns all query parameters
   */
  getQueryParameters(operation, path, contentType, api) {
    return this.getParametersByType('query', operation, path, contentType, api);
  }

  /**
   * returns all path parameters
   */
  getPathParameters(operation, path, contentType, api) {
    return this.getParametersByType('path', operation, path, contentType, api);
  }

  /**
   * returns all header parameters
   */
  getHeaderParameters(operation, path, contentType, api) {
    return this.getParametersByType('header', operation, path, contentType, api);
  }

  /**
   * returns all cookie parameters
   */
  getCookieParameters(operation, path, contentType, api) {
    return this.getParametersByType('cookie', operation, path, contentType, api);
  }

  /**
   * returns all parameters of the specified type
   */
  getParametersByType(type, operation, path, contentType, api) {
    let parameters = this.getParameters(operation, path, contentType, api);

    if (!parameters || parameters.length === 0) {
      return [];
    }

    return Object.keys(parameters).filter(function (parameterName) {
      return parameters[parameterName].in === type;
    });
  }

  resolveRelativeUrl(url) {
    if (url && !/(wss?|https?):\/\//.test(url)) {
      url = this.buildFullUrl(window.location.host + '/', url);
    }

    return url;
  }

  findSelectedEndpoint(selectedEndpoint, endpoints) {
    const foundEndpoint = endpoints.find((endpoint) => selectedEndpoint === this.resolveRelativeUrl(endpoint.url));
    return foundEndpoint ? foundEndpoint : null;
  }

  getServerVariables(openApi, selectedEndpoint, options) {
    if (!openApi || !this.isSupported(openApi)) {
      return;
    }
    let endpoint;
    let path = options.path;
    let method = options.method;
    if (get(openApi, `paths[${path}][${method}].servers`)) {
      endpoint = this.findSelectedEndpoint(selectedEndpoint, openApi.paths[path][method].servers);
    } else if (get(openApi, `paths[${path}].servers`)) {
      endpoint = this.findSelectedEndpoint(selectedEndpoint, openApi.paths[path].servers);
    } else if (openApi.endpoint) {
      if (openApi.endpoint.servers) {
        endpoint = this.findSelectedEndpoint(selectedEndpoint, openApi.endpoint.servers);
      }
    } else if (get(openApi, '[x-ibm-configuration][servers]', []).length > 0) {
      endpoint = this.findSelectedEndpoint(selectedEndpoint, openApi['x-ibm-configuration']['servers']);
    } else if (get(openApi, '[x-ibm-endpoints]', []).length > 0) {
      endpoint = this.findSelectedEndpoint(selectedEndpoint, openApi['x-ibm-endpoints']);
    } else if (openApi.servers && openApi.servers.length > 0) {
      endpoint = this.findSelectedEndpoint(selectedEndpoint, openApi.servers);
    }
    if (endpoint) {
      var variables = endpoint.variables;
    }
    return variables;
  }

  getFullOauthUrls(urls, endpoint) {
    let newUrls = {};
    if (urls.authorizationUrl && urls.authorizationUrl.length > 0) {
      // substitute out any $(catalog.host) variable
      newUrls.authorizationUrl = this.substituteHost(urls.authorizationUrl, endpoint);
      if (endpoint && !/(wss?|https?):\/\//.test(newUrls.authorizationUrl)) {
        newUrls.authorizationUrl = endpoint + '/' + newUrls.authorizationUrl;
        newUrls.authorizationUrl = newUrls.authorizationUrl.replace(/([^:])(\/\/+)/g, '$1/');
      }
    }

    if (urls.tokenUrl && urls.tokenUrl.length > 0) {
      // substitute out any $(catalog.host) variable
      newUrls.tokenUrl = this.substituteHost(urls.tokenUrl, endpoint);
      if (endpoint && !/(wss?|https?):\/\//.test(newUrls.tokenUrl)) {
        newUrls.tokenUrl = endpoint + '/' + newUrls.tokenUrl;
        newUrls.tokenUrl = newUrls.tokenUrl.replace(/([^:])(\/\/+)/g, '$1/');
      }
    }

    if (urls.refreshUrl && urls.refreshUrl.length > 0) {
      // substitute out any $(catalog.host) variable
      newUrls.refreshUrl = this.substituteHost(urls.refreshUrl, endpoint);
      if (endpoint && !/(wss?|https?):\/\//.test(newUrls.refreshUrl)) {
        newUrls.refreshUrl = endpoint + '/' + newUrls.refreshUrl;
        newUrls.refreshUrl = newUrls.refreshUrl.replace(/([^:])(\/\/+)/g, '$1/');
      }
    }

    return newUrls;
  }

  resolveOauthUrls(securityDefinitions, endpoint) {
    let definitions = JSON.parse(JSON.stringify(securityDefinitions));

    Object.keys(definitions).forEach((securityDef) => {
      if (definitions[securityDef].type === 'oauth2') {
        if (typeof definitions[securityDef].flow === 'string') {
          let urls = this.getFullOauthUrls(definitions[securityDef], endpoint);
          for (const [key, val] of Object.entries(urls)) {
            definitions[securityDef][key] = val;
          }
        } else if (definitions[securityDef].flows) {
          Object.keys(definitions[securityDef].flows).forEach((flow) => {
            let urls = this.getFullOauthUrls(definitions[securityDef].flows[flow], endpoint);
            for (const [key, val] of Object.entries(urls)) {
              definitions[securityDef].flows[flow][key] = val;
            }
          });
        }
      }
    });
    return definitions;
  }

  /**
   * return all security information about an operation.
   * includes securityDefinitions and securityFlows
   */
  getSecurityConfig(openApi, operation, credentials, endpoint) {
    if (!openApi || !this.isSupported(openApi)) {
      return;
    }

    let config = {};

    config.securityDefinitions = this.getSecurityDefinitions();

    let securityFlows = operation.security || openApi.security;

    if (securityFlows) {
      let allFlows = {};

      securityFlows.forEach((flow) => {
        let label = Object.keys(flow)
          .filter(function (key) {
            return key.indexOf('$$') !== 0;
          })
          .join(', ');

        let flowConfig = {};

        if (!isEmpty(flow)) {
          flowConfig.flow = flow;

          flowConfig.config = this.getSecurityFlowConfig(flow, config.securityDefinitions, credentials, endpoint);

          allFlows[label] = flowConfig;
        }
      });

      config.securityFlows = allFlows;

      // set a default security flow.
      let flowNames = Object.keys(config.securityFlows);
      config.flags = flowNames.length < 1 ? null : config.securityFlows[flowNames[0]].config;
    }

    // TODO: port this from ApiOperation.configureOperation...
    // if (localProps.options.fixedCredentials && localProps.options.fixedCredentials.clientId) {
    //   newState.clientId = localProps.options.fixedCredentials.clientId;
    // } else if (localProps.credentials && localProps.credentials.clientId) {
    //   newState.clientId = localProps.credentials.clientId;
    // }
    // if (localProps.options.fixedCredentials && localProps.options.fixedCredentials.clientSecret) {
    //   newState.clientSecret = localProps.options.fixedCredentials.clientSecret;
    // } else if (localProps.credentials && localProps.credentials.clientSecret) {
    //   newState.clientSecret = localProps.credentials.clientSecret;
    // }
    // if (localProps.credentials && localProps.credentials.redirectUri) {
    //   newState.redirectUri = localProps.credentials.redirectUri;
    // }
    //
    // if (localProps.options.authentication && localProps.options.authentication.username) {
    //   newState.username = localProps.options.authentication.username;
    // }
    // if (localProps.options.authentication && localProps.options.authentication.password) {
    //   newState.password = localProps.options.authentication.password;
    // }

    return config;
  }

  /**
   * Checks if a definition has been specified.
   * Logs a warning if not rather than dying horribly.
   */
  securityDefinitionFound(definitions, securityDef) {
    if (!definitions) {
      console.warn(securityDef + ' specified but no security definitions found.');
      return false;
    }
    let thisDef = definitions[securityDef];
    if (!thisDef) {
      console.warn('no security definition found: ' + thisDef);
      return false;
    }

    return true;
  }

  /**
   * Returns the default flow configuration object
   */
  getSecurityFlowConfigDefaults() {
    return {
      clientSecretLocation: null,
      clientSecretName: null,
      clientIdLocation: null,
      clientIdName: null,
      httpScheme: null,
      oauthFlow: null,
      oauthFlows: null,
      oauthFlowsConfig: null,
      oauthAuthUrl: null,
      oauthTokenUrl: null,
      oauthScopes: null,
      oauthRefreshUrl: null,

      requiresClientId: false,
      requiresClientSecret: false,
      requiresBasicAuth: false,
      requiresDigestAuth: false,
      requiresHobaAuth: false,
      requiresScramShaAuth: false,
      requiresVapidAuth: false,
      requiresNegotiateAuth: false,
      requiresMutualAuth: false,
      requiresHttpAuth: false,
      requiresOauth: false,

      requiresSecuritySection: false,
      requiresIdentificationSection: false,
      requiresAuthorizationSection: false,
      requiresRefreshToken: false,
      requiresUserCredentials: false,
      requiresRedirectUri: false,

      externalApiKeys: []
    };
  }

  /**
   * Sets the config for the apiKey security type definition
   */
  setApikeySecurityTypeConfig(config, securityDef) {
    if (securityDef.type === 'apiKey') {
      if (
        securityDef.name === 'client_id' ||
        securityDef.name === 'X-IBM-Client-Id' ||
        (securityDef['x-key-type'] && (securityDef['x-key-type'] === 'clientId' || securityDef['x-key-type'] === 'client_id'))
      ) {
        config.requiresClientId = true;
        config.clientIdLocation = securityDef['in'];
        config.clientIdName = securityDef['name'];
      } else if (
        securityDef.name === 'client_secret' ||
        securityDef.name === 'X-IBM-Client-Secret' ||
        (securityDef['x-key-type'] && (securityDef['x-key-type'] === 'clientSecret' || securityDef['x-key-type'] === 'client_secret'))
      ) {
        config.requiresClientSecret = true;
        config.clientSecretLocation = securityDef['in'];
        config.clientSecretName = securityDef['name'];
      } else {
        // it's an external api key
        config.externalApiKeys[securityDef] = securityDef;
      }
    }

    return config;
  }

  /**
   * Sets any remaining config options based off of values
   * previously set
   */
  setRemainingConfig(config, credentials) {
    // any security requirements at all?
    if (
      (config.requiresClientId && (!credentials || !credentials.clientId)) ||
      (config.requiresClientSecret && (!credentials || !credentials.clientSecret || credentials.clientSecret === 'SECRET')) ||
      config.requiresBasicAuth ||
      config.requiresOauth ||
      config.requiresDigestAuth ||
      config.requiresHobaAuth ||
      config.requiresScramShaAuth ||
      config.requiresVapidAuth ||
      config.requiresNegotiateAuth ||
      config.requiresMutualAuth ||
      Object.keys(config.externalApiKeys).length > 0
    ) {
      config.requiresSecuritySection = true;
    }

    // any requirement for identification?
    if (config.requiresClientId || config.requiresClientSecret) {
      config.requiresIdentificationSection = true;
    }

    // any requirement for identification from external api keys?
    if (Object.keys(config.externalApiKeys).length > 0) {
      config.requiresIdentificationSection = true;
    }

    // any requirement for authorization?
    if (
      config.requiresBasicAuth ||
      config.oauthFlow === 'password' ||
      config.requiresDigestAuth ||
      config.requiresHobaAuth ||
      config.requiresScramShaAuth ||
      config.requiresVapidAuth ||
      config.requiresNegotiateAuth ||
      config.requiresOauth ||
      config.requiresMutualAuth
    ) {
      config.requiresAuthorizationSection = true;
    }

    // any requirement for user credentials?
    if (config.requiresBasicAuth || config.oauthFlow === 'password') {
      config.requiresUserCredentials = true;
    }

    // any requirement for token refresh?
    if (config.oauthFlow === 'accessCode' || config.oauthFlow === 'authorizationCode' || config.oauthFlow === 'password') {
      config.requiresRefreshToken = true;
    }

    return config;
  }

  substituteHost(url, endpoint) {
    if (!url || url.indexOf(CATALOG_URL) < 0 || !endpoint) {
      return url;
    }

    // TODO - is this possible, why isn't the host stamped in on stage/publish?
    // return 'TODO:substitute host in oauth endpoint';
    //
    // let endpoint = (this.props.endpoint === "swaggerEndpoint") ?
    //  this.scheme + "://" + this.state.host : this.props.endpoint;
    let newUrl = url.replace(CATALOG_URL, endpoint);
    if (/((wss?|https?):\/\/){2}/.test(newUrl)) {
      if (/wss?:\/\//.test(newUrl)) {
        newUrl = newUrl.replace(/wss?:\/\//, '');
      } else {
        newUrl = newUrl.replace(/https?:\/\//, '');
      }
    }
    return newUrl;
  }

  getDefaultAcceptHeader(operation, api) {
    let defaultAccept = 'application/json';
    let accepts = this.getAccepts(operation, api);

    if (accepts.indexOf('application/json') !== -1) {
      return defaultAccept;
    } else if (accepts.length > 0) {
      return accepts[0];
    } else {
      return defaultAccept;
    }
  }

  getDefaultContentTypeHeader(operation, api) {
    let defaultContentType = 'application/json';
    let contenttypes = this.getContentTypes(operation, api);

    if (contenttypes.indexOf('application/json') !== -1) {
      return defaultContentType;
    } else if (contenttypes.length > 0) {
      return contenttypes[0];
    } else {
      return defaultContentType;
    }
  }

  /**
   * returns a de-referenced object if present
   */
  getLocalReference(ref, openApi) {
    if (!ref || !openApi) {
      return;
    }

    if (ref.indexOf('#') === 0) {
      ref = ref.substring(1);
    }

    let dereffed = jsonPointer.get(openApi, ref);
    if (dereffed) {
      return dereffed;
    } else {
      return {
        error: 'missing reference',
        $ref: ref
      };
    }
  }

  /**
   * returns an array of x- extensions from the selected object
   */
  getCustomExtensions(object) {
    let extensions = {};

    if (object) {
      let ignoredKeys = [
        'x-pathalias',
        'x-embedded-doc',
        'x-example',
        'x-try-it-out-enabled',
        'x-codegen-config',
        'x-sdk-operations',
        'x-sdk-global-param',
        'x-scopeValidate'
      ];
      let matchedKeys = Object.keys(object).filter(function (key) {
        return !!(key.startsWith('x-') && !key.startsWith('x-ibm-') && !ignoredKeys.includes(key));
      });

      matchedKeys.map(function (key) {
        extensions[key] = object[key];
        return null;
      });
    }

    if (Object.keys(extensions).length > 0) {
      return extensions;
    }

    return null;
  }

  /**
   * get api tags if being used
   */
  getApiTags(openApi) {
    if (!openApi || !this.isSupported(openApi)) {
      return;
    }

    let apiTags = {
      tags: {},
      operations: []
    };

    if (openApi.tags) {
      openApi.tags.forEach((tag) => {
        apiTags.tags[tag.name] = {
          tags: {},
          operations: []
        };

        if (tag.description) {
          apiTags.tags[tag.name].description = tag.description;
        }

        if (tag.externalDocs && tag.externalDocs.url) {
          // if no Url it will be invalid OAI and not displayed
          apiTags.tags[tag.name].externalDocs = tag.externalDocs;
        }
      });
    }
    return apiTags;
  }

  getProtocols(servers) {
    let protocols = [];
    if (servers) {
      servers.forEach((endpoint) => {
        let url = this.resolveRelativeUrl(endpoint.url);
        let scheme = url.substring(0, url.indexOf('://'));
        if (scheme !== '') {
          protocols.push(scheme);
        }
      });
    }
    return protocols;
  }

  /**
   * get schemes if available or return scheme of where we are running.
   */
  getSchemes(openApi) {
    if (!openApi || !this.isSupported(openApi)) {
      return;
    }

    let schemes = [];

    if (openApi.schemes) {
      schemes = openApi.schemes;
    } else {
      schemes = schemes.concat(this.getProtocols(openApi.servers));
      if (openApi.paths) {
        Object.keys(openApi.paths).forEach((path) => {
          schemes = schemes.concat(this.getProtocols(openApi.paths[path].servers));
          Object.keys(openApi.paths[path]).forEach((operation) => {
            schemes = schemes.concat(this.getProtocols(openApi.paths[path][operation].servers));
          });
        });
      }
      if (schemes.length === 0) {
        schemes = [window.location.protocol.replace(':', '')];
      }
    }
    schemes = uniq(schemes);
    return schemes;
  }

  getDataTableRows(dataObject) {
    let rowsArray = [];
    Object.entries(dataObject).forEach(([key, value]) => rowsArray.push({ id: key, data: value }));

    return rowsArray;
  }

  /**
   * Constructs a fully qualified url from the provided endpoint and operation path.
   * Prevents double '/' where endpoint ends with / and opPath starts with one.
   *
   * @param endpoint
   * @param opPath
   * @returns {*}
   */
  buildFullUrl(endpoint, opPath) {
    if (endpoint.endsWith('/') && opPath.startsWith('/')) {
      endpoint = endpoint.substr(0, endpoint.length - 1);
    }
    return endpoint + opPath;
  }

  mapProtocol(protocol) {
    const SUPPORTED_PROTOCOLS = {
      kafka: 'Kafka',
      'kafka-secure': 'Kafka'
    };
    let mappedProtocol = SUPPORTED_PROTOCOLS[protocol];
    if (mappedProtocol) return mappedProtocol;
    else return protocol;
  }

  isEnforced() {
    return get(this.api, 'x-ibm-configuration.enforced', false);
  }
}
